import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/utils/errors';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { teamApi } from '@/services/api';
import { DocumentationLink } from '@/types';

interface DocumentationTableProps {
  teamId: string;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

type DocType = 'confluence' | 'github_wiki' | 'external';

const EMPTY_FORM = {
  url: '',
  title: '',
  doc_type: 'external' as DocType,
  description: '',
};

export default function DocumentationTable({ teamId, onNotify }: DocumentationTableProps) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DocumentationLink | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: documentation } = useQuery({
    queryKey: ['team-documentation', teamId],
    queryFn: () => teamApi.getDocumentation(teamId).then((res) => res.data),
    enabled: !!teamId,
  });

  const addMutation = useMutation({
    mutationFn: (data: Partial<DocumentationLink>) => teamApi.addDocumentation(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-documentation', teamId] });
      onNotify('Documentation added successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to add documentation: ${getErrorMessage(error)}`, 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DocumentationLink> }) =>
      teamApi.updateDocumentation(teamId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-documentation', teamId] });
      onNotify('Documentation updated successfully!', 'success');
      handleClose();
    },
    onError: (error: Error) => {
      onNotify(`Failed to update documentation: ${getErrorMessage(error)}`, 'error');
    },
  });

  const removeDocMutation = useMutation({
    mutationFn: (docId: string) => teamApi.removeDocumentation(teamId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-documentation', teamId] });
      onNotify('Documentation removed successfully!', 'success');
    },
    onError: (error: Error) => {
      onNotify(`Failed to remove documentation: ${getErrorMessage(error)}`, 'error');
    },
  });

  const handleOpen = (item?: DocumentationLink) => {
    if (item) {
      setEditingItem(item);
      setForm({
        url: item.url,
        title: item.title || '',
        doc_type: item.doc_type || 'external',
        description: item.description || '',
      });
    } else {
      setEditingItem(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = () => {
    const data: Partial<DocumentationLink> = {
      url: form.url,
      title: form.title || undefined,
      doc_type: form.doc_type,
      description: form.description || undefined,
    };
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      addMutation.mutate(data);
    }
  };

  const handleRemove = (docId: string) => {
    if (window.confirm('Are you sure you want to remove this documentation link?')) {
      removeDocMutation.mutate(docId);
    }
  };

  const isSaving = addMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Documentation
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>URL</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documentation?.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>{doc.title || 'Untitled'}</TableCell>
                <TableCell>
                  <Chip label={doc.doc_type || 'external'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                    {doc.url}
                  </a>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit documentation">
                    <IconButton size="small" onClick={() => handleOpen(doc)} sx={{ mr: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove documentation">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(doc.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {documentation?.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No documentation added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Documentation' : 'Add Documentation'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="URL"
              value={form.url}
              onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
              required
              fullWidth
              placeholder="https://..."
            />
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.doc_type}
                label="Type"
                onChange={(e) => setForm((prev) => ({ ...prev, doc_type: e.target.value as DocType }))}
              >
                <MenuItem value="confluence">Confluence</MenuItem>
                <MenuItem value="github_wiki">GitHub Wiki</MenuItem>
                <MenuItem value="external">External</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !form.url.trim()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
